import React from 'react';
import { useApp } from '../../../context/AppContext';
import { StatCard, Card, CardHeader, CardTitle, Table, Tr, Td, Badge, Btn, SectionHead } from '../../common/UI';
import { generateDeliveryNotePDF } from '../../../utils/pdfGenerator';

export default function Dashboard({ onNav }) {
  const { stock, dns, pendingStock, rejectedStock, activeStock, approveStock, openModal, role, loggedUser } = useApp();
  const name = loggedUser?.name?.split(' ')[0] || 'User';

  return (
    <div className="fade-up" style={{ padding: 24 }}>
      <SectionHead
        title={`Good morning, ${name} 👋`}
        subtitle="Here's your warehouse overview for today">
      </SectionHead>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
        <StatCard icon="📦" iconBg="#fff7ed" value={pendingStock.length} valueColor="var(--warning)" label="Pending Approval" change="↑ 2 new" changeType="up" />
        <StatCard icon="✅" iconBg="#f0fdf4" value={activeStock.length} valueColor="var(--success)" label="Active Stock" change="+8 today" changeType="up" />
        <StatCard icon="🚚" iconBg="#eff6ff" value={dns.length} valueColor="var(--accent)" label="Delivery Orders" change="Today" changeType="neutral" />
        <StatCard icon="❌" iconBg="#fff5f5" value={rejectedStock.length} valueColor="var(--danger)" label="Rejected Items" change={`${rejectedStock.length} need fix`} changeType="down" />
      </div>

      {/* Two columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Recent Intakes */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Intakes</CardTitle>
            <span onClick={() => onNav('stock-list')}
              style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>
              View all →
            </span>
          </CardHeader>
          <Table headers={['PO Number', 'Vessel', 'Customer', 'Weight', 'Status']}>
            {[...stock].sort((a, b) => b.id - a.id).slice(0, 5).map(s => (
              <Tr key={s.id}>
                <Td strong>{s.po}</Td>
                <Td>{s.vessel}</Td>
                <Td>{s.customer}</Td>
                <Td>{s.weight} kg</Td>
                <Td><Badge status={s.status} /></Td>
              </Tr>
            ))}
          </Table>
        </Card>

        {/* Pending Approvals (admin only) */}
        {role === 'admin' && (
          <Card>
            <CardHeader>
              <CardTitle>⏳ Pending Approvals</CardTitle>
              <span onClick={() => onNav('approvals')}
                style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>
                Manage →
              </span>
            </CardHeader>
            <div style={{ padding: 12 }}>
              {pendingStock.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>All caught up!</div>
                </div>
              ) : pendingStock.slice(0, 3).map(item => (
                <div key={item.id} style={{
                  border: '1px solid var(--border)', borderRadius: 10,
                  marginBottom: 8, overflow: 'hidden', boxShadow: 'var(--shadow)'
                }}>
                  <div style={{
                    padding: '10px 12px', background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8
                  }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: 7, background: '#fff',
                      border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 14, flexShrink: 0
                    }}>
                      {item.type === 'Offland' ? '🔄' : '📦'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{item.type} — {item.po}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                        {item.vessel} · {item.customer} · {item.qty} pcs
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <Btn size="xs" variant="success" onClick={() => approveStock(item.id)}>✓</Btn>
                      <Btn size="xs" variant="danger" onClick={() => openModal('reject', item)}>✕</Btn>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Latest DNs */}
      <Card>
        <CardHeader>
          <CardTitle>📄 Latest Delivery Notes</CardTitle>
          <span onClick={() => onNav('delivery')}
            style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>
            View all →
          </span>
        </CardHeader>
        <Table headers={['DN Number', 'Customer', 'Vessel', 'Destination', 'Packages', 'Weight', 'Status', '']}>
          {dns.map(dn => (
            <Tr key={dn.id}>
              <Td mono accent>{dn.dnNumber}</Td>
              <Td strong>{dn.customer}</Td>
              <Td>{dn.vessel}</Td>
              <Td>{dn.destination}</Td>
              <Td>{dn.packages} pkgs</Td>
              <Td>{dn.totalWeight} kg</Td>
              <Td><Badge status={dn.status} /></Td>
              <Td>
                <span onClick={() => generateDeliveryNotePDF(dn, dn.items)}
                  style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>↓ PDF</span>
              </Td>
            </Tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}
